import { Module, GetterTree, MutationTree, ActionTree } from "vuex";
import { RootState } from "@/store/types";
import { Shift, ShiftService } from "@/api";

type ShiftState = {
  shifts: Shift[] | null;
};

const state: ShiftState = {
  shifts: null,
};

const getters: GetterTree<ShiftState, RootState> = {
  shifts: state => state.shifts,
};

const mutations: MutationTree<ShiftState> = {
  SET_SHIFTS(state, shifts: Shift[]) {
    state.shifts = shifts;
  },
  ADD_SHIFT(state, shift: Shift) {
    if (state.shifts == null) {
      state.shifts = [];
    }
    state.shifts.push(shift);
  },
  UPDATE_SHIFT(state, updatedShift: Shift) {
    if (state.shifts) {
      const index = state.shifts.findIndex(
        shift => shift.id === updatedShift.id,
      );
      if (index !== -1) {
        state.shifts.splice(index, 1, updatedShift);
      }
    }
  },
  REMOVE_SHIFT(state, shiftId: number) {
    if (state.shifts) {
      const index = state.shifts.findIndex(shift => shift.id === shiftId);
      if (index !== -1) {
        state.shifts.splice(index, 1);
      }
    }
  },
};

const actions: ActionTree<ShiftState, RootState> = {
  async fetchShifts({ commit, rootState }) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const shifts = await ShiftService.getShiftListByCompany({ companyId });
      commit("SET_SHIFTS", shifts);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async addShift({ commit, rootState }, shift: Shift) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      shift.companyId = companyId;
      const newShift = await ShiftService.addShift({
        companyId,
        requestBody: shift,
      });
      commit("ADD_SHIFT", newShift);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async updateShift({ commit, rootState }, shift: Shift) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      shift.companyId = companyId;
      const updatedShift = await ShiftService.updateShift({
        companyId,
        shiftId: shift.id,
        requestBody: shift,
      });
      commit("UPDATE_SHIFT", updatedShift);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async removeShift({ commit, rootState }, shiftId: number) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      await ShiftService.archiveShift({
        companyId,
        shiftId,
      });
      commit("REMOVE_SHIFT", shiftId);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
};

export const shift: Module<ShiftState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
